/**
 * PartnerProfilePanel - read-only profile for an assigned partner
 * organisation (a contractor / installer / dealer, whatever the tenant's
 * external role is called).
 *
 * Rendered beneath the partner picker in the registration wizard and on
 * the registration record itself, so an agent can confirm who is attached
 * without navigating away (WPS-163).
 *
 * Everything is driven by ``PartnershipAttribute`` metadata served on the
 * partnership detail endpoint - the panel has no knowledge of which
 * attributes a tenant defines, what they are called, or how they group.
 * Each attribute's ``configuration_data.group`` block supplies:
 *
 *   key      - group identity
 *   label    - section heading (and the row label for `joined` groups)
 *   order    - section order within the panel
 *   layout   - `rows` (label/value pairs), `joined` (all values on one
 *              line), or `table` (value is a list of objects)
 *   span     - `half` (two groups share a band) or `full`
 *   columns  - value columns for a `rows` group; 1 renders label-left /
 *              value-right, more renders label-above-value cells
 *
 * Layout is container-query driven (`@container` on the panel root), because
 * the same panel renders both in a full-width card on the registration record
 * and in the wizard's narrow right-hand column - a viewport-keyed `md:` would
 * put two columns inside a 340px cell. Half-span bands only pair up past
 * `@xl`, so the wizard column stacks them rather than squeezing two label
 * columns into ~250px each.
 *
 * Attributes with no group block fall into a trailing ungrouped section,
 * so a tenant whose schema predates the group metadata still gets a
 * usable panel instead of an empty one. Empty values and empty groups are
 * dropped, so tenants that seed only a subset show only what they have.
 */
import { type ReactNode, useMemo } from 'react'
import { Mail, Phone, UserPlus } from 'lucide-react'

import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { useFormatting } from '@/admin/hooks/use-formatting'
import type {
  Partnership,
  PartnershipAttributeValue,
} from '@/admin/types/organisations'
import { Badge } from '@/shared/components/ui/badge'
import { cn } from '@/shared/lib/utils'
import {
  defineField,
  formatDisplayValue,
  formatPhoneDisplay,
  isDisplayEmpty,
} from '@/shared/lib/forms'

// ---------------------------------------------------------------------------
// Group metadata
// ---------------------------------------------------------------------------

type GroupLayout = 'rows' | 'joined' | 'table'

interface GroupMeta {
  key: string
  label: string
  order: number
  layout: GroupLayout
  span: 'half' | 'full'
  columns: number
}

interface ProfileGroup extends GroupMeta {
  attributes: PartnershipAttributeValue[]
}

/** Fallback for attributes whose schema carries no group block yet. */
const UNGROUPED: GroupMeta = {
  key: '__ungrouped__',
  label: '',
  order: Number.MAX_SAFE_INTEGER,
  layout: 'rows',
  span: 'full',
  columns: 2,
}

function readGroupMeta(attr: PartnershipAttributeValue): GroupMeta {
  const raw = (attr.configuration_data ?? {})['group']
  if (!raw || typeof raw !== 'object') return UNGROUPED
  const group = raw as Record<string, unknown>
  const key = typeof group.key === 'string' ? group.key : ''
  if (!key) return UNGROUPED
  const layout = group.layout
  const columns = Number(group.columns)
  return {
    key,
    label: typeof group.label === 'string' ? group.label : '',
    order: Number.isFinite(Number(group.order)) ? Number(group.order) : UNGROUPED.order,
    layout:
      layout === 'joined' || layout === 'table' || layout === 'rows'
        ? layout
        : 'rows',
    span: group.span === 'half' ? 'half' : 'full',
    columns: Number.isFinite(columns) && columns > 0 ? Math.trunc(columns) : 1,
  }
}

/**
 * Rows in a `table` group carry a `columns` list on the attribute's
 * configuration_data, so the table's headers are tenant data too.
 */
interface TableColumnMeta {
  key: string
  label: string
}

function readTableColumns(attr: PartnershipAttributeValue): TableColumnMeta[] {
  const raw = (attr.configuration_data ?? {})['columns']
  if (!Array.isArray(raw)) return []
  return raw.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return []
    const col = entry as Record<string, unknown>
    if (typeof col.key !== 'string' || !col.key) return []
    return [
      {
        key: col.key,
        label: typeof col.label === 'string' ? col.label : col.key,
      },
    ]
  })
}

// ---------------------------------------------------------------------------
// Value helpers
// ---------------------------------------------------------------------------

/** Contact field types carry a small icon, so an email or phone reads at a
 *  glance. Keyed by field type, never by which attribute a tenant defines. */
const CONTACT_ICONS: Record<string, typeof Mail> = { EMAIL: Mail, PHONE: Phone }

function formatAttributeValue(
  attr: PartnershipAttributeValue,
  locale: string,
): ReactNode {
  const Icon = CONTACT_ICONS[String(attr.field_type ?? '').toUpperCase()]
  const value = formatDisplayValue(
    defineField({
      code_name: attr.code_name,
      display_name: attr.display_name,
      field_type: attr.field_type,
      configuration_data: attr.configuration_data,
    }),
    attr.value,
    { locale },
  )
  if (!Icon) return value
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      <Icon aria-hidden="true" className="h-4 w-4 shrink-0 text-[var(--ink-2)]" />
      <span className="min-w-0 break-words">{value}</span>
    </span>
  )
}

/** Text-only rendering, used where values are joined into one line. */
function plainValue(attr: PartnershipAttributeValue): string {
  const value = attr.value
  if (value === null || value === undefined) return ''
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value).trim()
}

// ---------------------------------------------------------------------------
// Section renderers
// ---------------------------------------------------------------------------

// One standard with every other detail card (Clara Vale): sentence-case
// labels in the secondary ink, values in the primary ink, nothing below 14px.
const LABEL_CLASS = 'text-[14px] text-[var(--ink-2)]'
const VALUE_CLASS = 'text-[15px] text-[var(--ink)]'
const HEADING_CLASS = 'text-[14px] font-medium text-[var(--ink)]'
// A soft tile for label-above-value cells: a whisper of the ink colour, so it
// works on any tenant theme without a hard grey box.
const TILE_CLASS =
  'flex min-w-0 flex-col gap-1 rounded-xl bg-[color-mix(in_srgb,var(--ink)_4%,transparent)] px-4 py-3'

function GroupHeading({ label }: { label: string }) {
  if (!label) return null
  return <span className={HEADING_CLASS}>{label}</span>
}

function RowsGroup({ group, locale }: { group: ProfileGroup; locale: string }) {
  // A single value column reads best as label-left / value-right pairs;
  // wider groups put the label above the value so the columns line up.
  if (group.columns <= 1) {
    return (
      <div className="flex flex-col">
        {group.attributes.map((attr) => (
          <div
            key={attr.code_name}
            className="flex items-baseline justify-between gap-3 border-b-[0.5px] border-b-[var(--rule-light)] py-[10px] last:border-b-0"
          >
            <span className={LABEL_CLASS}>{attr.display_name}</span>
            <span className={`${VALUE_CLASS} text-right`}>
              {formatAttributeValue(attr, locale)}
            </span>
          </div>
        ))}
      </div>
    )
  }
  return (
    <div
      className={`grid gap-3 ${
        group.columns >= 4
          ? '@xs:grid-cols-2 @2xl:grid-cols-4'
          : group.columns === 3
            ? '@xs:grid-cols-2 @2xl:grid-cols-3'
            : '@xs:grid-cols-2'
      }`}
    >
      {group.attributes.map((attr) => (
        <div key={attr.code_name} className={TILE_CLASS}>
          <span className={LABEL_CLASS}>{attr.display_name}</span>
          <span className={`${VALUE_CLASS} break-words`}>
            {formatAttributeValue(attr, locale)}
          </span>
        </div>
      ))}
    </div>
  )
}

function JoinedGroup({ group }: { group: ProfileGroup }) {
  const joined = group.attributes
    .map(plainValue)
    .filter((part) => part.length > 0)
    .join(', ')
  if (!joined) return null
  return (
    <div className={TILE_CLASS}>
      <GroupHeading label={group.label} />
      <span className={VALUE_CLASS}>{joined}</span>
    </div>
  )
}

interface CertificationRow extends Record<string, unknown> {
  id: string
}

function TableGroup({ group }: { group: ProfileGroup }) {
  const attr = group.attributes[0]
  const columns = readTableColumns(attr)
  const rows: CertificationRow[] = useMemo(() => {
    const value = attr?.value
    if (!Array.isArray(value)) return []
    return value.flatMap((entry, index) => {
      if (!entry || typeof entry !== 'object') return []
      return [{ ...(entry as Record<string, unknown>), id: String(index) }]
    })
  }, [attr?.value])

  if (!attr || columns.length === 0 || rows.length === 0) return null

  const tableColumns: DataTableColumn<CertificationRow>[] = columns.map((col) => ({
    id: col.key,
    header: col.label,
    cell: (row) => {
      const raw = row[col.key]
      return raw === null || raw === undefined || raw === ''
        ? '-'
        : String(raw)
    },
  }))

  return (
    <div className="flex flex-col gap-1.5">
      <GroupHeading label={group.label} />
      <DataTable
        tableId={`partner-profile-${attr.code_name}`}
        columns={tableColumns}
        data={rows}
        rowKey="id"
        dense
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

/**
 * `card` (default) renders flush inside a `DetailCard` body, on the same
 * background and padding as every other card's rows, so the partner card
 * does not read as a grey box inside a white card. `inset` keeps the boxed
 * treatment for where the panel sits loose under a form field (the wizard's
 * partner picker) and needs its own edge.
 */
type PanelVariant = 'card' | 'inset'

const PANEL_CLASS: Record<PanelVariant, string> = {
  card: 'bg-[var(--bg)] px-5 py-4',
  inset:
    'rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-2.5',
}

export function PartnerProfilePanel({
  partnership,
  variant = 'card',
  className,
}: {
  partnership: Partnership
  variant?: PanelVariant
  className?: string
}) {
  const { locale } = useFormatting()

  const groups = useMemo<ProfileGroup[]>(() => {
    const attributes = (partnership.attribute_values ?? []).filter(
      (attr) => !isDisplayEmpty(attr.value),
    )
    const byKey = new Map<string, ProfileGroup>()
    for (const attr of attributes) {
      const meta = readGroupMeta(attr)
      const existing = byKey.get(meta.key)
      if (existing) {
        existing.attributes.push(attr)
      } else {
        byKey.set(meta.key, { ...meta, attributes: [attr] })
      }
    }
    return [...byKey.values()]
      .map((group) => ({
        ...group,
        attributes: [...group.attributes].sort((a, b) => a.order - b.order),
      }))
      .sort((a, b) => a.order - b.order)
  }, [partnership.attribute_values])

  // Consecutive half-span groups share a band so they render side by side,
  // the way the membership / company blocks do in OC's profile.
  const bands = useMemo<ProfileGroup[][]>(() => {
    const out: ProfileGroup[][] = []
    for (const group of groups) {
      const last = out[out.length - 1]
      if (group.span === 'half' && last?.length === 1 && last[0].span === 'half') {
        last.push(group)
      } else {
        out.push([group])
      }
    }
    return out
  }, [groups])

  const contacts = partnership.contacts ?? []
  // A partner captured by hand is badged, because its profile is only as good
  // as what the agent typed - unlike a network-synced partner, nothing else
  // maintains it.
  const isManual = partnership.origin === 'MANUAL'

  // A partner with no profile attributes at all (a bare record, or a tenant
  // that seeds no partner schema) would otherwise render nothing, leaving the
  // record with no sign of who is attached. Fall back to identity only.
  if (groups.length === 0 && contacts.length === 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-between gap-3',
          PANEL_CLASS[variant],
          className,
        )}
      >
        <span className={VALUE_CLASS}>
          {partnership.downstream_organisation.name}
        </span>
        <div className="flex items-center gap-2">
          {isManual && <ManualBadge />}
          <span className={LABEL_CLASS}>
            {partnership.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        '@container flex flex-col gap-5',
        PANEL_CLASS[variant],
        className,
      )}
    >
      {/* The company name lives on the organisation rather than in the
        attribute groups, so it needs rendering explicitly - without this the
        card showed an address and contacts with no indication of who they
        belong to. Paired with the provenance badge, as in the mockup. */}
      <div className="flex items-center gap-3">
        <span
          aria-hidden="true"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[color-mix(in_srgb,var(--green)_10%,transparent)] text-[15px] font-medium text-[var(--green)]"
        >
          {initialsOf(partnership.downstream_organisation.name)}
        </span>
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-[18px] leading-[1.25] font-medium text-[var(--ink)]">
            {partnership.downstream_organisation.name}
          </span>
          {/* No status pill here: the platform's partnership status can
              disagree with a membership status the tenant's own profile
              shows below, and a card must never contradict itself. */}
          {isManual && <ManualBadge />}
        </div>
      </div>
      {bands.map((band) => (
        <div
          key={band.map((g) => g.key).join('-')}
          className={band.length > 1 ? 'grid gap-3 @xl:grid-cols-2' : undefined}
        >
          {band.map((group) => (
            <GroupSection key={group.key} group={group} locale={locale} />
          ))}
        </div>
      ))}
      {contacts.length > 0 && <ContactsSection contacts={contacts} />}
    </div>
  )
}

/**
 * Marks a partner someone typed in rather than one the partner network
 * supplied - a reader should know the profile is only as good as what was
 * entered. Sentence case with an icon: the phrase is too long to survive the
 * uppercase, wide-tracked treatment the short field labels use.
 */
function ManualBadge() {
  return (
    <Badge
      variant="outline"
      className="w-fit gap-1.5 rounded-full border-[var(--rule-strong)] bg-[var(--bg)] px-2.5 py-1 text-[14px] font-normal normal-case tracking-normal text-[var(--ink-2)]"
    >
      <UserPlus className="h-3 w-3 shrink-0 text-[var(--green)]" />
      New contractor added manually
    </Badge>
  )
}

/**
 * Named people at the partner. Only manually captured partners carry these -
 * a network-synced partner's contacts live in the network of record - so the
 * section is absent rather than empty for them.
 */
function ContactsSection({
  contacts,
}: {
  contacts: NonNullable<Partnership['contacts']>
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className={LABEL_CLASS}>
        {contacts.length === 1 ? 'Primary contact' : 'Contacts'}
      </span>
      <div className="grid gap-3 @xl:grid-cols-2">
        {contacts.map((contact) => (
          <div key={contact.uuid} className="flex min-w-0 flex-col gap-0.5">
            {contact.name && (
              <span className={VALUE_CLASS}>
                {contact.name}
                {contact.is_primary && contacts.length > 1 ? ' (primary)' : ''}
              </span>
            )}
            {contact.email && (
              <span className="break-words text-[12px] text-[var(--ink-2)]">
                {contact.email}
              </span>
            )}
            {contact.phone && (
              <span className="text-[12px] text-[var(--ink-2)]">
                {/* Same formatter the company phone goes through, so the two
                    do not render one formatted and one raw on the same card. */}
                {formatPhoneDisplay(contact.phone) || contact.phone}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function GroupSection({
  group,
  locale,
}: {
  group: ProfileGroup
  locale: string
}) {
  if (group.layout === 'joined') return <JoinedGroup group={group} />
  if (group.layout === 'table') return <TableGroup group={group} />
  return (
    <div className="flex min-w-0 flex-col gap-2.5">
      {group.columns > 1 && <GroupHeading label={group.label} />}
      <RowsGroup group={group} locale={locale} />
    </div>
  )
}

/** Up to two initials for the company monogram ("Amos Exteriors" -> "AE"). */
function initialsOf(name: string): string {
  const words = name
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter((w) => w && !/^(inc|llc|ltd|co|corp|the)$/i.test(w))
  return (words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')
}
